/**
 * Job applications.
 *
 * Résumés are streamed from a private storage root through an authenticated endpoint and every
 * download is audited — the file is never exposed at a guessable public URL.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Select } from '@/components/ui';
import { DataTable, type Column } from '@/components/admin/DataTable';
import { LoadError } from '@/components/site/blocks';
import { useDebounced } from '@/hooks';
import { cmsApi } from '@/services/api';
import { ApiError } from '@/services/client';
import type { ApplicationRecord } from '@/services/types';

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'screening', label: 'Screening' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'interview', label: 'Interview' },
  { value: 'offer', label: 'Offer' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Not selected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];


export function ApplicationsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const debouncedSearch = useDebounced(search, 300);

  const listQuery = useQuery({
    queryKey: ['cms', 'applications', { debouncedSearch, status, page }],
    queryFn: () =>
      cmsApi.applications({ search: debouncedSearch || undefined, status: status || undefined, page, page_size: 20 }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: { status?: string; rating?: number } }) =>
      cmsApi.updateApplication(id, payload),
    onSuccess: () => {
      setNotice('Application updated.');
      setError('');
      void queryClient.invalidateQueries({ queryKey: ['cms', 'applications'] });
      void queryClient.invalidateQueries({ queryKey: ['cms', 'dashboard'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'The update failed.'),
  });

  const columns: Column<ApplicationRecord>[] = [
    { key: 'reference', header: 'Reference', width: '11rem', render: (row) => <span className="font-mono text-xs">{row.reference}</span> },
    {
      key: 'full_name',
      header: 'Applicant',
      sortable: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{row.full_name}</p>
          <p className="truncate text-xs text-muted">
            <a href={`mailto:${row.email}`}>{row.email}</a> · {row.phone}
          </p>
        </div>
      ),
    },
    { key: 'job_title', header: 'Role', render: (row) => <span className="text-ink-soft">{row.job_title}</span> },
    {
      key: 'total_experience_years',
      header: 'Experience',
      sortable: true,
      width: '7rem',
      render: (row) => <span className="text-muted">{row.total_experience_years} yrs</span>,
    },
    {
      key: 'status',
      header: 'Stage',
      width: '11rem',
      render: (row) => (
        <Select
          value={row.status}
          aria-label={`Stage for ${row.full_name}`}
          onChange={(event) => updateMutation.mutate({ id: row.id, payload: { status: event.target.value } })}
          className="h-9 py-1 text-xs"
        >
          {STATUSES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      ),
    },
    {
      key: 'resume',
      header: 'Résumé',
      align: 'right',
      width: '7rem',
      render: (row) => (
        <a href={row.resume_url} className="text-sm font-medium no-underline hover:underline">
          Download
        </a>
      ),
    },
  ];

  if (listQuery.isError) return <LoadError onRetry={() => void listQuery.refetch()} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">Job applications</h1>
        <p className="mt-1 text-sm text-muted">
          Every application submitted through the careers pages, with the résumé attached.
        </p>
      </div>

      {notice && <Alert tone="success">{notice}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      <Alert tone="info" title="Handle these records carefully">
        Applications contain personal data. Downloads are audited, and records should be retained only for as long as
        your published privacy policy states.
      </Alert>

      <DataTable
        caption="Job applications"
        columns={columns}
        rows={listQuery.data?.results ?? []}
        count={listQuery.data?.count ?? 0}
        page={listQuery.data?.page ?? 1}
        pages={listQuery.data?.pages ?? 1}
        isLoading={listQuery.isLoading}
        search={search}
        searchPlaceholder="Search by reference, name, email or employer…"
        filters={[
          {
            key: 'status',
            label: 'Stage',
            options: [{ value: '', label: 'All stages' }, ...STATUSES],
          },
        ]}
        filterValues={{ status }}
        emptyTitle="No applications yet"
        emptyDescription="Applications submitted through a role page appear here immediately."
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        onFilter={(_key, value) => {
          setStatus(value);
          setPage(1);
        }}
        onPage={setPage}
      />
    </div>
  );
}
